import { collection, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Mirrors iOS PinSeenTracker — tracks which pins the user has tapped so
// unseen pins can show the pulsing ring. Signed-in users sync to
// users/{uid}/seenPins; guests fall back to localStorage (the web
// equivalent of the iOS UserDefaults cache).
const LOCAL_KEY = '2ez_seen_pins';

export class PinSeenService {
  private static getLocal(): Set<string> {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  }

  private static saveLocal(ids: Set<string>) {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(Array.from(ids)));
    } catch {
      // Storage unavailable — keep in-memory only
    }
  }

  // Loads seen pins: local cache first, then merges Firestore for signed-in users
  static async loadSeenPins(userId: string | null): Promise<Set<string>> {
    const local = this.getLocal();
    if (!userId) return local;

    try {
      const snapshot = await getDocs(collection(db, 'users', userId, 'seenPins'));
      snapshot.forEach(d => {
        const pinId = d.data().pinId;
        local.add(typeof pinId === 'string' && pinId ? pinId : d.id);
      });
      this.saveLocal(local);
    } catch (e) {
      console.error('Failed to load seen pins, using local cache:', e);
    }
    return local;
  }

  // Marks a pin seen: local always (covers guests), Firestore when signed in.
  // Returns the updated set for state updates.
  static markPinAsSeen(pinId: string, userId: string | null): Set<string> {
    if (!pinId) return this.getLocal();

    const ids = this.getLocal();
    ids.add(pinId);
    this.saveLocal(ids);

    if (userId) {
      setDoc(
        doc(db, 'users', userId, 'seenPins', pinId),
        { pinId, seenAt: serverTimestamp() },
        { merge: true }
      ).catch(e => console.error('Failed to mark pin as seen:', e));
    }

    return ids;
  }
}
